import React from "react"
import { cn } from "../lib/utils"

export type ButtonVariant = "primary" | "secondary" | "outline" | "brand-outline" | "ghost" | "danger"
export type ButtonSize = "sm" | "md" | "lg" | "icon"

interface MyButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  buttonClasses?: string
  disableDefaultStyles?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  outline: "border border-border bg-surface text-foreground hover:bg-surface-muted",
  // Smartlead's secondary action: brand outline + brand text on a plain surface.
  "brand-outline": "border border-primary text-primary bg-transparent hover:bg-primary-soft",
  ghost: "bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground",
  danger: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm min-h-[34px]",
  md: "px-4 py-2 text-sm min-h-[42px]",
  lg: "px-8 py-3 text-base min-h-[46px]",
  icon: "p-2 min-h-[36px]",
}

export default function Button({
  children,
  buttonClasses,
  className,
  disableDefaultStyles,
  variant,
  size,
  ...props
}: MyButtonProps) {
  if (disableDefaultStyles) {
    return (
      <button
        {...props}
        className={`${buttonClasses ?? ""} ${className ?? ""}`}
      >
        {children}
      </button>
    )
  }

  const variantClass = variant ? variantClasses[variant] : ""
  // Callers that pass neither variant nor size historically relied on the
  // 42px min-height alone and brought their own padding — keep that.
  const sizeClass = size ? sizeClasses[size] : (variant ? sizeClasses.md : "min-h-[42px]")

  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center gap-2 text-center text-sm font-medium outline-none cursor-pointer rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        variantClass,
        sizeClass,
        "w-fit",
        buttonClasses,
        className,
      )}
    >
      {children}
    </button>
  )
}
